import React from 'react';
import { Button } from '../ui/button';
import { Code, Zap, PlayCircle, ArrowRight, Server, Database, Lock } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import SEO from '../SEO';

const APITesting = ({ onBack, onLogin, onRegister }) => {
  return (
    <PageLayout onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="API Testing - Automated REST & GraphQL Testing | Lexa.run"
        description="Comprehensive API testing for REST, GraphQL, and SOAP. Validate endpoints, responses, and performance with AI-powered automation."
      />

      <main className="container mx-auto px-4 sm:px-6 py-12 pt-24">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Code className="h-4 w-4 mr-2" />
            API Testing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Automated API Testing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Test your REST, GraphQL, and SOAP APIs with AI-powered automation. Validate responses, performance, and security effortlessly.
          </p>
          <Button onClick={onRegister} size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
            <PlayCircle className="h-5 w-5 mr-2" />
            Start Testing Now
          </Button>
        </div>

        {/* Key Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">What You Can Test</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <Server className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">REST APIs</h3>
              <p className="text-gray-600">Test RESTful endpoints with GET, POST, PUT, DELETE, and PATCH methods. Validate status codes and responses.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Code className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">GraphQL</h3>
              <p className="text-gray-600">Test GraphQL queries, mutations, and subscriptions. Validate schema and response data structure.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <Database className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Response Validation</h3>
              <p className="text-gray-600">Automatically validate JSON/XML responses, headers, and data types against expected schemas.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Performance Testing</h3>
              <p className="text-gray-600">Measure response times, throughput, and identify performance bottlenecks in your APIs.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Authentication</h3>
              <p className="text-gray-600">Test OAuth, JWT, API keys, Basic Auth, and Bearer tokens. Ensure secure API access.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-4">
                <Code className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Error Handling</h3>
              <p className="text-gray-600">Validate error responses, status codes, and error messages for edge cases and failures.</p>
            </div>
          </div>
        </div>

        {/* Supported API Types */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-12 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Supported API Types</h2>
          <p className="text-center text-gray-600 mb-8">Test any API protocol with comprehensive support</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">🔗</div>
              <h4 className="font-bold">REST</h4>
              <p className="text-sm text-gray-600">RESTful APIs</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">📊</div>
              <h4 className="font-bold">GraphQL</h4>
              <p className="text-sm text-gray-600">Queries & Mutations</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">🧼</div>
              <h4 className="font-bold">SOAP</h4>
              <p className="text-sm text-gray-600">SOAP Services</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">⚡</div>
              <h4 className="font-bold">WebSocket</h4>
              <p className="text-sm text-gray-600">Real-time APIs</p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Define API Endpoints</h3>
              <p className="text-gray-600">Specify your API endpoints, methods, headers, and request payloads using our intuitive interface.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Set Validations</h3>
              <p className="text-gray-600">Configure response validations, assertions, and expected outcomes. AI helps suggest common checks.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Run & Monitor</h3>
              <p className="text-gray-600">Execute tests and get detailed reports with response times, payloads, and validation results.</p>
            </div>
          </div>
        </div>

        {/* Integrations Section - New Design */}
        <div className="mb-16 bg-white rounded-2xl p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fits into your existing workflow
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                With comprehensive integrations for <span className="text-blue-600 font-semibold">CI/CD</span>, <span className="text-blue-600 font-semibold">issue tracking</span>, and <span className="text-blue-600 font-semibold">test case management</span>, Lexa fits into your current development and testing process with minimal disruption.
              </p>
            </div>

            {/* Right Column - Integration Logos */}
            <div>
              <div className="grid grid-cols-3 gap-4">
                {/* Row 1 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/poehpjw0_image.png" alt="Azure DevOps" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/32/Atlassian_Bitbucket_Logo.png" alt="Bitbucket" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/tdtq4iim_image.png" alt="CircleCI" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 2 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/lbn2nq9z_image.png" alt="Discord" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/GitLab_logo.svg" alt="GitLab" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 3 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/r25r80p5_image.png" alt="Jenkins" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/hymy3wvl_image.png" alt="TeamCity" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/mc6i5gez_image.png" alt="Jira" className="h-12 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Automate Your API Testing?</h2>
          <p className="text-xl mb-8 opacity-90">Start testing in minutes, not days. No credit card required.</p>
          <Button onClick={onRegister} size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
            Start Free Trial
          </Button>
        </div>
      </main>
    </PageLayout>
  );
};

export default APITesting;
